import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../services/firebase';
import { getQuizHistory, getWeakAreas, getLearningHistory, markQuizAsReviewed } from '../services/firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import ProgressChart from '../components/ProgressChart';
import RecommendationCard from '../components/RecommendationCard';

const Dashboard = () => {
  const navigate = useNavigate();
  // Add this state at the top with other states
  const [quizHistory, setQuizHistory] = useState([]);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [timeSpent, setTimeSpent] = useState(0);
  const [improveTab, setImproveTab] = useState('weak');
  const [learningHistory, setLearningHistory] = useState([]);
  const [weakAreas, setWeakAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedTopicSessions, setSelectedTopicSessions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showTopicModal, setShowTopicModal] = useState(false);
  const [stats, setStats] = useState({
    totalQuizzes: 0,
    averageScore: 0,
    streak: 0,
    hoursLearned: 0,
    topicsLearned: 0,
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      const userId = auth.currentUser?.uid;
      if (!userId) {
        setError('User not authenticated');
        setLoading(false);
        return;
      }

      let quizzes = [];
      let sessions = [];
      let weak = [];

      try {
        quizzes = await getQuizHistory(userId, 10);
        console.log('Quiz History:', quizzes);
      } catch (err) {
        console.error('Error fetching quiz history:', err);
      }

      try {
        sessions = await getLearningHistory(userId, 20);
        console.log('Learning History:', sessions);
      } catch (err) {
        console.error('Error fetching learning history:', err);
      }

      try {
        weak = await getWeakAreas(userId);
        console.log('Weak Areas:', weak);
      } catch (err) {
        console.error('Error fetching weak areas:', err);
      }

      setQuizHistory(quizzes);
      setLearningHistory(sessions);
      setWeakAreas(weak);

      const uniqueTopics = new Set();
      sessions.forEach(s => {
        if (s.topic) uniqueTopics.add(s.topic);
      });

      if (quizzes.length > 0) {
        const avgScore = quizzes.reduce((acc, q) => acc + (q.score || 0), 0) / quizzes.length;
        setStats({
          totalQuizzes: quizzes.length,
          averageScore: Math.round(avgScore),
          streak: calculateStreak(sessions, quizzes),
          hoursLearned: Math.round((quizzes.length + sessions.length) * 0.3),
          topicsLearned: uniqueTopics.size,
        });
      } else if (sessions.length > 0) {
        setStats({
          totalQuizzes: 0,
          averageScore: 0,
          streak: calculateStreak(sessions, quizzes),
          hoursLearned: Math.round(sessions.length * 0.3),
          topicsLearned: uniqueTopics.size,
        });
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      setError('Failed to load dashboard data. Please refresh the page.');
      setLoading(false);
    }
  };

  const fetchTopicSessions = async (topic) => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) return;

      const sessionsRef = collection(db, 'users', userId, 'sessions');
      const q = query(
        sessionsRef,
        where('topic', '==', topic),
        where('type', '==', 'explanation'),
        orderBy('timestamp', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const sessions = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      console.log('Topic Sessions:', sessions);
      return sessions;
    } catch (error) {
      console.error('Error fetching topic sessions:', error);
      return [];
    }
  };

  const calculateStreak = (sessions, quizzes) => {
    const allActivities = [...sessions, ...quizzes].sort((a, b) => {
      const dateA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
      const dateB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp);
      return dateB - dateA;
    });

    if (allActivities.length === 0) return 0;

    let streak = 1;
    for (let i = 0; i < allActivities.length - 1; i++) {
      const currentDate = allActivities[i].timestamp?.toDate
        ? allActivities[i].timestamp.toDate()
        : new Date(allActivities[i].timestamp);
      const nextDate = allActivities[i + 1].timestamp?.toDate
        ? allActivities[i + 1].timestamp.toDate()
        : new Date(allActivities[i + 1].timestamp);

      currentDate.setHours(0, 0, 0, 0);
      nextDate.setHours(0, 0, 0, 0);

      const dayDiff = Math.floor((currentDate - nextDate) / (1000 * 60 * 60 * 24));

      if (dayDiff === 1) {
        streak++;
      } else if (dayDiff > 1) {
        break;
      }
    }

    return streak;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Recently';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (err) {
      return 'Recently';
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    } catch (err) {
      return '';
    }
  };

  const handleViewSession = (session) => {
    setSelectedSession(session);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedSession(null);
  };

  const closeTopicModal = () => {
    setShowTopicModal(false);
    setSelectedTopicSessions([]);
  };

  // Add this function after closeTopicModal
  const handleReviewQuiz = async (quizData) => {
    setSelectedQuiz(quizData);
    setShowQuizModal(true);

    // Mark as reviewed
    try {
      const userId = auth.currentUser?.uid;
      if (userId && quizData.id) {
        await markQuizAsReviewed(userId, quizData.id);
      }
    } catch (error) {
      console.error('Error marking quiz as reviewed:', error);
    }
  };

  const closeQuizModal = () => {
    setShowQuizModal(false);
    setSelectedQuiz(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <div className="spinner mb-4"></div>
        <p className="text-slate-400">Loading your dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="card max-w-md text-center">
          <h2 className="text-xl font-bold mb-4 text-red-400">Error</h2>
          <p className="text-slate-300 mb-6">{error}</p>
          <button onClick={loadDashboardData} className="btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container-custom py-8">
      {/* Single Session Modal */}
      {showModal && selectedSession && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={closeModal}>
          <div className="card max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold">{selectedSession.topic}</h2>
              <button onClick={closeModal} className="text-slate-400 hover:text-white transition">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4 flex items-center gap-4 text-sm text-slate-400">
              <span>📅 {formatDate(selectedSession.timestamp)}</span>
              <span>🕐 {formatTime(selectedSession.timestamp)}</span>
              {selectedSession.selectedStyle && (
                <span className="badge badge-info">{selectedSession.selectedStyle}</span>
              )}
            </div>

            {selectedSession.explanations && (
              <div className="space-y-6">
                {selectedSession.explanations.simple && (
                  <div className="bg-slate-700 rounded-lg p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-2xl">🎯</span>
                      <h3 className="text-lg font-semibold">Simple Explanation</h3>
                    </div>
                    <p className="text-slate-200 leading-relaxed">{selectedSession.explanations.simple}</p>
                  </div>
                )}

                {selectedSession.explanations.detailed && (
                  <div className="bg-slate-700 rounded-lg p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-2xl">📚</span>
                      <h3 className="text-lg font-semibold">Detailed Explanation</h3>
                    </div>
                    <p className="text-slate-200 leading-relaxed">{selectedSession.explanations.detailed}</p>
                  </div>
                )}

                {selectedSession.explanations.analogy && (
                  <div className="bg-slate-700 rounded-lg p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-2xl">💡</span>
                      <h3 className="text-lg font-semibold">Analogy Explanation</h3>
                    </div>
                    <p className="text-slate-200 leading-relaxed">{selectedSession.explanations.analogy}</p>
                  </div>
                )}
              </div>
            )}

            <div className="mt-6 flex gap-4 justify-end">
              <button onClick={closeModal} className="btn-outline">
                Close
              </button>
              <button onClick={() => navigate('/tutor')} className="btn-primary">
                Learn More Topics
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Topic Sessions Modal */}
      {showTopicModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={closeTopicModal}>
          <div className="card max-w-6xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">
                {selectedTopicSessions[0]?.topic || 'Topic'} - All Sessions
              </h2>
              <button onClick={closeTopicModal} className="text-slate-400 hover:text-white transition">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {selectedTopicSessions.length > 0 ? (
              <div className="space-y-6">
                {selectedTopicSessions.map((session, index) => (
                  <div key={session.id} className="bg-slate-700 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">Session {index + 1}</h3>
                      <div className="text-sm text-slate-400">
                        <span className="mr-3">📅 {formatDate(session.timestamp)}</span>
                        <span>🕐 {formatTime(session.timestamp)}</span>
                      </div>
                    </div>

                    {session.explanations && (
                      <div className="space-y-4">
                        {session.explanations.simple && (
                          <div className="bg-slate-800 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xl">🎯</span>
                              <h4 className="font-semibold">Simple</h4>
                            </div>
                            <p className="text-slate-200 text-sm leading-relaxed">{session.explanations.simple}</p>
                          </div>
                        )}

                        {session.explanations.detailed && (
                          <div className="bg-slate-800 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xl">📚</span>
                              <h4 className="font-semibold">Detailed</h4>
                            </div>
                            <p className="text-slate-200 text-sm leading-relaxed">{session.explanations.detailed}</p>
                          </div>
                        )}

                        {session.explanations.analogy && (
                          <div className="bg-slate-800 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xl">💡</span>
                              <h4 className="font-semibold">Analogy</h4>
                            </div>
                            <p className="text-slate-200 text-sm leading-relaxed">{session.explanations.analogy}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-slate-400">No sessions found for this topic</p>
              </div>
            )}

            <div className="mt-6 flex gap-4 justify-end">
              <button onClick={closeTopicModal} className="btn-outline">
                Close
              </button>
              <button onClick={() => navigate('/tutor')} className="btn-primary">
                Learn More
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Welcome Section */}
      <div className="mb-8 fade-in-down">
        <h1 className="text-3xl font-bold mb-2">
          Welcome back, {auth.currentUser?.displayName?.split(' ')[0] || 'Student'}! 👋
        </h1>
        <p className="text-slate-400">Here's your learning progress overview</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <div className="card card-entrance-1 hover-lift">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Total Quizzes</p>
              <p className="text-3xl font-bold mt-1">{stats.totalQuizzes}</p>
            </div>
            <div className="bg-indigo-500 bg-opacity-20 p-3 rounded-lg">
              <svg className="w-8 h-8 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="card card-entrance-2 hover-lift">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Average Score</p>
              <p className="text-3xl font-bold mt-1">{stats.averageScore}%</p>
            </div>
            <div className="bg-green-500 bg-opacity-20 p-3 rounded-lg">
              <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
          </div>
        </div>

        <div className="card card-entrance-3 hover-lift">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Day Streak</p>
              <p className="text-3xl font-bold mt-1">{stats.streak} 🔥</p>
            </div>
            <div className="bg-orange-500 bg-opacity-20 p-3 rounded-lg">
              <svg className="w-8 h-8 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="card card-entrance-4 hover-lift">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Hours Learned</p>
              <p className="text-3xl font-bold mt-1">{stats.hoursLearned}h</p>
            </div>
            <div className="bg-purple-500 bg-opacity-20 p-3 rounded-lg">
              <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
          </div>
        </div>

        <div className="card card-entrance-4 hover-lift">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Topics Learned</p>
              <p className="text-3xl font-bold mt-1">{stats.topicsLearned}</p>
            </div>
            <div className="bg-pink-500 bg-opacity-20 p-3 rounded-lg">
              <svg className="w-8 h-8 text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div onClick={() => navigate('/tutor')} className="card-hover">
          <div className="flex items-center gap-4">
            <div className="bg-indigo-500 p-4 rounded-lg">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-semibold">Start Learning</h3>
              <p className="text-slate-400 text-sm">Get AI-powered explanations</p>
            </div>
          </div>
        </div>

        <div onClick={() => navigate('/quiz')} className="card-hover">
          <div className="flex items-center gap-4">
            <div className="bg-purple-500 p-4 rounded-lg">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-semibold">Take a Quiz</h3>
              <p className="text-slate-400 text-sm">Test your knowledge</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 rounded-lg font-medium transition whitespace-nowrap ${activeTab === 'overview'
            ? 'bg-indigo-500 text-white'
            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
        >
          📊 Overview
        </button>
        <button
          onClick={() => setActiveTab('learning')}
          className={`px-4 py-2 rounded-lg font-medium transition whitespace-nowrap ${activeTab === 'learning'
            ? 'bg-indigo-500 text-white'
            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
        >
          📚 Learning History ({learningHistory.length})
        </button>
        <button
          onClick={() => setActiveTab('tips')}
          className={`px-4 py-2 rounded-lg font-medium transition whitespace-nowrap ${activeTab === 'tips'
            ? 'bg-indigo-500 text-white'
            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
        >
          💡 Study Tips
        </button>
      </div>
      {/* Tab Content */}
      {activeTab === 'overview' && (
        <>
          <div className="card mb-8 fade-in">
            <h2 className="text-xl font-semibold mb-4">Performance Trend</h2>
            <ProgressChart quizHistory={quizHistory} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Areas to Improve with Dual Option */}
            <div className="card fade-in-left">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Areas to Improve</h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => setImproveTab('weak')}
                    className={`px-3 py-1 rounded text-sm font-medium transition ${improveTab === 'weak'
                        ? 'bg-red-500 text-white'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`}
                  >
                    Weak Areas
                  </button>
                  <button
                    onClick={() => setImproveTab('practice')}
                    className={`px-3 py-1 rounded text-sm font-medium transition ${improveTab === 'practice'
                        ? 'bg-blue-500 text-white'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`}
                  >
                    Need Practice
                  </button>
                </div>
              </div>

              {improveTab === 'weak' ? (
                weakAreas.length > 0 ? (
                  <div className="space-y-3">
                    {weakAreas.slice(0, 5).map((area, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between hover-lift p-3 bg-slate-700 rounded-lg cursor-pointer"
                        onClick={() => navigate('/quiz')}
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <span className="text-2xl">📉</span>
                          <div className="flex-1">
                            <span className="text-slate-300 font-medium block">{area.topic}</span>
                            <span className="text-xs text-slate-500">Below 60% score</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="badge badge-warning">{area.count} attempts</span>
                          <button className="btn-primary text-xs px-3 py-1">
                            Retry →
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-2">🎯</div>
                    <p className="text-slate-400">No weak areas identified!</p>
                    <p className="text-sm text-slate-500 mt-1">Keep up the great work</p>
                  </div>
                )
              ) : (
                learningHistory.length > 0 ? (
                  <div className="space-y-3">
                    {learningHistory.slice(0, 5).map((session, index) => {
                      // Find if this topic has a quiz
                      const relatedQuiz = quizHistory.find(q => q.topic === session.topic);

                      return (
                        <div
                          key={index}
                          className="flex items-center justify-between hover-lift p-3 bg-slate-700 rounded-lg cursor-pointer"
                          onClick={() => navigate('/quiz')}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <span className="text-2xl">📝</span>
                            <div className="flex-1">
                              <span className="text-slate-300 font-medium block">{session.topic}</span>
                              <span className="text-xs text-slate-500">
                                {relatedQuiz
                                  ? `Last quiz: ${relatedQuiz.score}%`
                                  : 'No quiz taken yet'}
                              </span>
                            </div>
                          </div>
                          <button className="btn-primary text-xs px-3 py-1">
                            Test →
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-2">📚</div>
                    <p className="text-slate-400">Start learning to practice!</p>
                    <button onClick={() => navigate('/tutor')} className="btn-primary mt-4">
                      Begin Learning
                    </button>
                  </div>
                )
              )}
            </div>

            {/* Recommended for You */}
            <div className="card fade-in-right">
              <h2 className="text-xl font-semibold mb-4">Recommended for You</h2>
              <RecommendationCard topic={weakAreas[0]?.topic || 'General Learning'} />
            </div>
          </div>
        </>
      )}

      {activeTab === 'learning' && (
        <div className="card fade-in">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Recent Learning Sessions</h2>
            <button onClick={loadDashboardData} className="text-sm text-indigo-400 hover:text-indigo-300">
              🔄 Refresh
            </button>
          </div>
          {learningHistory.length > 0 ? (
            <div className="space-y-3">
              {learningHistory.map((session) => (
                <div
                  key={session.id}
                  className="p-4 bg-slate-700 rounded-lg hover-lift cursor-pointer transition"
                  onClick={() => handleViewSession(session)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-lg">{session.topic || 'Untitled Topic'}</h3>
                    <div className="text-right">
                      <div className="text-xs text-slate-400">{formatDate(session.timestamp)}</div>
                      <div className="text-xs text-slate-500">{formatTime(session.timestamp)}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    {session.type === 'explanation' && (
                      <>
                        <span className="badge badge-info">Explanation</span>
                        {session.selectedStyle && (
                          <span className="text-sm text-slate-400">
                            📖 {session.selectedStyle}
                          </span>
                        )}
                        <span className="text-xs text-slate-500">Click to view</span>
                      </>
                    )}
                    {session.type === 'style_change' && (
                      <span className="badge badge-success">Style: {session.selectedStyle}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-slate-400 mb-4">No learning history yet</p>
              <button onClick={() => navigate('/tutor')} className="btn-primary">
                Start Learning Now
              </button>
            </div>
          )}
        </div>
      )}
      {activeTab === 'tips' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 fade-in">
          {/* General Study Tips */}
          <div className="card">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <span>📚</span> General Study Tips
            </h2>
            <div className="space-y-3">
              <div className="p-3 bg-slate-700 rounded-lg hover-lift">
                <h3 className="font-semibold text-indigo-400 mb-1">⏰ Create a Schedule</h3>
                <p className="text-sm text-slate-300">Study at the same time each day to build a consistent routine. Morning hours are often best for retention.</p>
              </div>
              <div className="p-3 bg-slate-700 rounded-lg hover-lift">
                <h3 className="font-semibold text-green-400 mb-1">✍️ Take Effective Notes</h3>
                <p className="text-sm text-slate-300">Use the Cornell method or mind maps. Review notes within 24 hours for better memory retention.</p>
              </div>
              <div className="p-3 bg-slate-700 rounded-lg hover-lift">
                <h3 className="font-semibold text-purple-400 mb-1">🎯 Set Clear Goals</h3>
                <p className="text-sm text-slate-300">Break down large topics into smaller, achievable daily goals. Track your progress regularly.</p>
              </div>
              <div className="p-3 bg-slate-700 rounded-lg hover-lift">
                <h3 className="font-semibold text-orange-400 mb-1">🔄 Practice Regularly</h3>
                <p className="text-sm text-slate-300">Review weak areas daily. Spaced repetition helps move knowledge to long-term memory.</p>
              </div>
            </div>
          </div>

          {/* CBSE/ICSE Exam Tips */}
          <div className="card">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <span>🎓</span> CBSE/ICSE Exam Tips
            </h2>
            <div className="space-y-3">
              <div className="p-3 bg-slate-700 rounded-lg hover-lift">
                <h3 className="font-semibold text-blue-400 mb-1">📖 NCERT is Your Bible</h3>
                <p className="text-sm text-slate-300">Read NCERT textbooks thoroughly. Board exams are primarily based on NCERT content and examples.</p>
              </div>
              <div className="p-3 bg-slate-700 rounded-lg hover-lift">
                <h3 className="font-semibold text-yellow-400 mb-1">📝 Practice Previous Papers</h3>
                <p className="text-sm text-slate-300">Solve last 10 years' board papers. Understand the question pattern and marking scheme.</p>
              </div>
              <div className="p-3 bg-slate-700 rounded-lg hover-lift">
                <h3 className="font-semibold text-pink-400 mb-1">⚡ Time Management</h3>
                <p className="text-sm text-slate-300">Allocate time based on marks: 1 mark = 1.5 minutes. Practice writing answers within time limits.</p>
              </div>
              <div className="p-3 bg-slate-700 rounded-lg hover-lift">
                <h3 className="font-semibold text-teal-400 mb-1">✨ Presentation Matters</h3>
                <p className="text-sm text-slate-300">Write neatly, underline keywords, draw diagrams. Good presentation can earn you extra marks.</p>
              </div>
            </div>
          </div>

          {/* Learning Techniques */}
          <div className="card">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <span>🧠</span> Learning Techniques
            </h2>
            <div className="space-y-3">
              <div className="p-3 bg-slate-700 rounded-lg hover-lift">
                <h3 className="font-semibold text-indigo-400 mb-1">🎨 Feynman Technique</h3>
                <p className="text-sm text-slate-300">Explain concepts in simple words as if teaching a 5-year-old. Identifies gaps in understanding.</p>
              </div>
              <div className="p-3 bg-slate-700 rounded-lg hover-lift">
                <h3 className="font-semibold text-green-400 mb-1">🍅 Pomodoro Method</h3>
                <p className="text-sm text-slate-300">Study for 25 minutes, then take a 5-minute break. Repeat 4 times, then take a longer break.</p>
              </div>
              <div className="p-3 bg-slate-700 rounded-lg hover-lift">
                <h3 className="font-semibold text-purple-400 mb-1">🔗 Active Recall</h3>
                <p className="text-sm text-slate-300">Test yourself without looking at notes. Much more effective than passive re-reading.</p>
              </div>
              <div className="p-3 bg-slate-700 rounded-lg hover-lift">
                <h3 className="font-semibold text-orange-400 mb-1">🗺️ Mind Mapping</h3>
                <p className="text-sm text-slate-300">Create visual diagrams connecting related concepts. Helps in understanding relationships.</p>
              </div>
            </div>
          </div>
          {/* Motivation & Wellness */}
          <div className="card">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <span>💪</span> Motivation & Wellness
            </h2>
            <div className="space-y-3">
              <div className="p-3 bg-slate-700 rounded-lg hover-lift">
                <h3 className="font-semibold text-blue-400 mb-1">😴 Sleep Well</h3>
                <p className="text-sm text-slate-300">Get 7-8 hours of sleep. Your brain consolidates memories during sleep. Never compromise on rest.</p>
              </div>
              <div className="p-3 bg-slate-700 rounded-lg hover-lift">
                <h3 className="font-semibold text-yellow-400 mb-1">🧘 Manage Stress</h3>
                <p className="text-sm text-slate-300">Practice meditation or deep breathing. Take regular breaks. Talk to friends and family.</p>
              </div>
              <div className="p-3 bg-slate-700 rounded-lg hover-lift">
                <h3 className="font-semibold text-pink-400 mb-1">🎯 Stay Motivated</h3>
                <p className="text-sm text-slate-300">Visualize your goals. Celebrate small wins. Remember why you started this journey.</p>
              </div>
              <div className="p-3 bg-slate-700 rounded-lg hover-lift">
                <h3 className="font-semibold text-teal-400 mb-1">🥗 Healthy Lifestyle</h3>
                <p className="text-sm text-slate-300">Eat nutritious food, stay hydrated, exercise regularly. A healthy body supports a healthy mind.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;